import { DataGridLayout, Filters, FiltersLayout, List, ListHeader, ListPageLayout, SearchInput } from 'framework/list';
import useAssetService, { READ_ASSET } from 'domain/finance/assets/useAssetService';
import CreateAssetButton from 'domain/finance/assets/shared/button/CreateAssetButton';
import AssetLink from 'domain/finance/assets/shared/field/AssetLink';
import ViewAssetButton from 'domain/finance/assets/shared/button/ViewAssetButton';
import { DateField, ExpandingField, SearchResultField, TextField } from 'framework/ui/field';
import AssetCategoryField from 'domain/finance/assets/shared/field/AssetCategoryField';
import AssetCategoryTypeField from 'domain/finance/assets/shared/field/AssetCategoryTypeField';
import AssetConditionField from 'domain/finance/assets/shared/field/AssetConditionField';
import PerformAssetCostProratingButton from 'domain/finance/assets/shared/button/PerformAssetCostProratingButton';
import { useAuthorization } from 'framework/authorization';
import { Box } from '@mui/material';
import { DATE_FORMAT } from 'framework/utils';
import AssetCategoryDropdownFilter from 'domain/finance/assets/shared/filter/AssetCategoryDropdownFilter';
import AssetTypeDropdownFilter from 'domain/finance/assets/shared/filter/AssetTypeDropdownFilter';
import AssetAssignmentFilter from 'domain/finance/assets/shared/filter/AssetAssignmentFilter';
import AssetStatusDropdownFilter from 'domain/finance/assets/shared/filter/AssetStatusDropdownFilter';
import {
  EmployeeLinkFieldWithNameLoading,
  EmployeeFilter,
  OfficeCodeField,
  OfficeCodeFilter,
} from 'domain/shared/employee';

const filter = (
  <Filters>
    <FiltersLayout>
      <AssetCategoryDropdownFilter label="Category" source="category" />
      <EmployeeFilter source="specification:currentAssignmentOwner" label="Current Owner" />
      <AssetTypeDropdownFilter label="Type" source="type" />
      <AssetAssignmentFilter label="Has owner" source="currentAssignmentStatus" />
      <AssetStatusDropdownFilter label="Status" source="status" />
      <OfficeCodeFilter label="Office" source="office" />
    </FiltersLayout>
  </Filters>
);

const AssetListPage = () => {
  const { search } = useAssetService();
  const { isGranted } = useAuthorization();

  return (
    <List getDataMethod={search} defaultSortField="latestAccountingCycle" defaultSortOrder="DESC">
      <ListPageLayout
        header={
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              h1: {
                mr: 'auto',
              },
              button: {
                marginTop: '10px',
                marginBottom: '20px',
                ml: 1,
              },
            }}
          >
            <ListHeader label="Assets" />
            <PerformAssetCostProratingButton />
          </Box>
        }
        filter={filter}
        search={<SearchInput label="inventory number or name" />}
        actions={[<CreateAssetButton />]}
      >
        <DataGridLayout buttons={[<ViewAssetButton source="inventoryNumber" isVisible={isGranted(READ_ASSET)} />]}>
          <AssetLink
            source="inventoryNumber"
            nameSource="inventoryNumber"
            label="Inventory number"
            width={150}
            isClickable={isGranted(READ_ASSET)}
            isSortable
          />
          <OfficeCodeField source="office" label="Office" width={125} isSortable />
          <AssetCategoryField source="category" label="Category" width={130} isSortable />
          <AssetCategoryTypeField source="type" label="Type" categorySource="category" width={130} isSortable />
          <SearchResultField source="name" label="Name" width={130} isSortable />
          <EmployeeLinkFieldWithNameLoading source="assignmentDetails.currentOwner" label="Current owner" width={150} />
          <AssetConditionField source="condition" label="Condition" width={130} isSortable />

          <DateField
            dateFormat={DATE_FORMAT.SIMPLE_MONTH}
            source="latestAccountingCycle"
            label="Latest amortization cycle"
            isSortable
            width={180}
          />

          <ExpandingField source="characteristics" width={260} maxValueLength={124}>
            <TextField source="characteristics" label="Characteristics" />
          </ExpandingField>
        </DataGridLayout>
      </ListPageLayout>
    </List>
  );
};

export default AssetListPage;
